import { Component, OnInit } from '@angular/core';
import moment from 'moment';
import { LazyLoadEvent, MessageService } from 'primeng/api';
import { finalize } from 'rxjs/operators';
import { BaseComponent } from 'src/app/shared/components/base.component';
import { WebStorageService } from 'src/app/shared/services/web-storage.service';
import { PaymentService } from 'src/app/shared/services/payment.service';
import { PaymentTransactionReportModel, PaymentTransactionRequestModel } from 'src/app/interfaces/payment-transaction';
import { DropdownModel } from 'src/app/shared/models/response/settings/drop-down-model';
import { AdminSettingsService } from '../../servicies/admin-settings.service';

@Component({
  selector: 'app-payment-transaction-logs',
  templateUrl: './payment-transaction-logs.component.html',
  styleUrls: ['./payment-transaction-logs.component.sass'],
  standalone: false
})
export class PaymentTransactionLogsComponent extends BaseComponent implements OnInit {
  searchRequest: PaymentTransactionRequestModel;
  items: PaymentTransactionReportModel[] = [];

  statuses = ['All', 'Approved', 'Declined'];
  dateRanges = {
    'Last 7 Days': [moment().subtract(6, 'days'), moment()],
    'Last 30 Days': [moment().subtract(29, 'days'), moment()],
    Yesterday: [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
    Today: [moment(), moment()],
    Tomorrow: [moment().add(1, 'days'), moment().add(1, 'days')],
    'This Month': [moment().startOf('month'), moment().endOf('month')],
    'Next 7 Days': [moment(), moment().add(6, 'days')],
    'Next 30 Days': [moment(), moment().add(29, 'days')],
    'Next Month': [moment().add(1, 'month').startOf('month'), moment().add(1, 'month').endOf('month')]
  };

  appDateFormat = this.webStorageService.getDateFormat();
  locale = {
    format: this.appDateFormat.dateFormat.toUpperCase(),
    displayFormat: this.appDateFormat.dateFormat.toUpperCase(),
    separator: ' To ',
    applyLabel: 'Apply'
  };

  totalRecords: number;

  selectedFilters: any[];
  branchOptions: DropdownModel[] = [];

  constructor(
    private webStorageService: WebStorageService,
    messageService: MessageService,
    private paymentService: PaymentService,
    private adminSettingsService: AdminSettingsService
  ) {
    super(messageService);
    this.totalRecords = 0;
  }

  ngOnInit() {
    this.setDefaultSearch();
    this.getBranches();
  }

  setDefaultSearch() {
    this.searchRequest = {
      transactionStatus: 'All',
      currentPageIndex: 0,
      firstRecordIndex: 0,
      pageSize: 20,
      transactionDate: { startDate: moment().subtract(29, 'days'), endDate: moment() },
      appointmentDate: null,
      branchIds: []
    };
  }

  setSelectedFilters() {
    this.selectedFilters = [];

    if (this.searchRequest.transactionStatus !== 'All') {
      this.selectedFilters.push({
        prop: 'transactionStatus',
        value: 'Transaction Status: ' + this.searchRequest.transactionStatus
      });
    }

    const transaction = this.searchRequest.transactionDate;
    if (transaction && transaction.startDate) {
      const value = transaction.startDate.format('MM/DD/YYYY') + ' To ' + transaction.endDate.format('MM/DD/YYYY');
      this.selectedFilters.push({
        prop: 'transactionDate',
        value: 'Transaction Date: ' + value
      });
    }

    const appointment = this.searchRequest.appointmentDate;
    if (appointment && appointment.startDate) {
      const value = appointment.startDate.format('MM/DD/YYYY') + ' To ' + appointment.endDate.format('MM/DD/YYYY');
      this.selectedFilters.push({
        prop: 'appointmentDate',
        value: 'Appointment Date: ' + value
      });
    }

    if (this.selectedFilters.length > 0) {
      this.selectedFilters.unshift({
        prop: null,
        value: 'Clear all filters'
      });
    }

    if (this.searchRequest.branchIds.length > 0) {
      this.selectedFilters.push({
        prop: 'branchIds',
        value: 'Branches: ' + this.searchRequest.branchIds.map((id) => {
          const branch = this.branchOptions.find((b) => b.value === id);
          return branch ? branch.name : '';
        }).join(', ')
      });
    }
  }

  getTotalRows() {
    if (this.searchRequest.pageSize) {
      return this.searchRequest.pageSize;
    }
    return this.totalRecords;
  }

  onSearch(event: any, element: any) {
    element.hide(event);
    this.loadData();
  }

  onClear(prop: string) {
    if (!prop) {
      this.setDefaultSearch();
    } else {
      const filterToRemove = this.selectedFilters.find((t) => t.prop === prop);
      this.selectedFilters = this.selectedFilters.filter((t) => t !== filterToRemove);
      switch (filterToRemove.prop) {
        case 'transactionStatus':
          this.searchRequest.transactionStatus = 'All';
          break;
        case 'transactionDate':
          this.searchRequest.transactionDate = null;
          break;
        case 'appointmentDate':
          this.searchRequest.appointmentDate = null;
          break;
        case 'branchIds':
          this.searchRequest.branchIds = [];
          break;
      }
    }

    this.loadData();
  }

  onLazyLoad(event: LazyLoadEvent) {
    this.searchRequest.currentPageIndex = event.first / 20;
    this.searchRequest.pageSize = 20;
    this.searchRequest.sortByColumnName = event.sortField;
    this.searchRequest.sortOrder = event.sortOrder === 1 ? 'ASC' : 'DESC';
    this.loadData();
  }

  private loadData() {
    this.setSelectedFilters();

    this.searchRequest.transactionDateFromString = null;
    this.searchRequest.transactionDateToString = null;
    this.searchRequest.appointmentDateFromString = null;
    this.searchRequest.appointmentDateToString = null;

    const transaction = this.searchRequest.transactionDate;
    if (transaction && transaction.startDate) {
      this.searchRequest.transactionDateFromString = transaction.startDate.format('YYYY/MM/DD');
      this.searchRequest.transactionDateToString = transaction.endDate.format('YYYY/MM/DD');
    }

    const appointment = this.searchRequest.appointmentDate;
    if (appointment && appointment.startDate) {
      this.searchRequest.appointmentDateFromString = appointment.startDate.format('YYYY/MM/DD');
      this.searchRequest.appointmentDateToString = appointment.endDate.format('YYYY/MM/DD');
    }

    this.loading = true;
    this.paymentService
      .getPaymentTransactionLogs(this.searchRequest)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe((result) => {
        const pagedResponse = result.responseModel;
        this.items = pagedResponse.results;
        this.totalRecords = pagedResponse.totalRecords;
      });
  }

  getBranches() {
    this.loading = true;
    this.adminSettingsService.GetBranchList()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe((result) => {
        this.branchOptions = result.responseModel.map(item => ({
          value: item.crmBranchId,
          name: item.crmBranchName
        }));
      });
  }

  export() {
    this.paymentService
      .exportPaymentTransactionLogs(this.searchRequest)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe(
        (result: Blob) => {
          const downloadedFile = new Blob([result], { type: result.type });

          const a = document.createElement('a');
          a.setAttribute('style', 'display:none;');
          a.download = 'PaymentTransactions_' + new Date().getTime().toString() + '.csv';
          document.body.appendChild(a);
          a.target = '_blank';
          a.href = URL.createObjectURL(downloadedFile);
          document.body.removeChild(a);
          a.click();
        },
        (httpResponseMessage) => {
          this.showErrorMessage(this.getErrorMessage(httpResponseMessage));
        }
      );
  }
}
